import { ref } from '@vue/composition-api'
import store from '@/store'
import useDocumentTemplate from '@/components/composables/useDocumentTemplate'
import _chunk from 'lodash/chunk'

// import TS interfaces
import { FormQuestionsObject } from '@/components/DynamicForms/DynamicFormsTypes'

const ATTRIBUTE_ID = 'termTemplate'

interface TermTemplateOptions {
  readonly school?: string
}

// The generateDefaultTemplate function generates a default template array,
// and this is to be used if the template array is not set (or is empty)
// on the termTemplate object.
// The reason this exists, is that there is currently no UI page for
// creating the term template, so it MUST be created on the backend, via our APIs.
// As a result, it is easier to use the API to JUST add/update questions,
// and allow the template to be generated dynamically.
function generateDefaultTemplate (questions: FormQuestionsObject = {}, columns: number = 2) {
  // remove partsOfTerm question from questions list as the partsOfTerm
  // question should be hardcoded as its own row below all other fields
  // and not included in dynamic template generation
  // generation
  const { partsOfTerm, ...questionsWithoutPartsOfTerm } = questions
  return _chunk(Object.values(questionsWithoutPartsOfTerm)
    .map(el => el.questionId), columns)
    .concat([ 'partsOfTerm' ])
}

/*
The generateDefaultPartOfTermTemplate function operates in the same way
as the generateDefaultTemplate, except it is intended for the
partsOfTermForm
*/
function generateDefaultPartOfTermTemplate (questions: FormQuestionsObject = {}, columns: number = 2) {
  // remove filters question from questions list as the filters
  // question should be hardcoded as its own row below all other fields
  // and not included in dynamic template generation
  // generation
  const { filters, ...questionsWithoutFilters } = questions
  return _chunk(Object.values(questionsWithoutFilters)
    .map(el => el.questionId), columns)
    .concat([ 'filters' ])
}

/*
The formatTermTemplateQuestions function dynamically adds
restrictions to specific date inputs based on the content
of the entity being changed
*/
function formatTermTemplateQuestions (termTemplateQuestions: FormQuestionsObject = {}, value = {}) {

  const questions = { ...termTemplateQuestions }

  // ensure all endDate questions have restrictions
  // related to the corresponding start date put in place
  const endDate = questions.endDate
  if (endDate) {
    questions.endDate = {
      ...endDate,
      config: {
        ...(endDate.config || {}),
        disabledDates: {
          to: '$$startDate',
        },
      },
    }
  }

  const effectiveEndDate = questions.effectiveEndDate
  if (effectiveEndDate) {
    questions.effectiveEndDate = {
      ...effectiveEndDate,
      config: {
        ...(effectiveEndDate.config || {}),
        disabledDates: {
          to: '$$effectiveStartDate',
        },
      },
    }
  }

  const examPeriodEndDate = questions.examPeriodEndDate
  if (examPeriodEndDate) {
    questions.examPeriodEndDate = {
      ...examPeriodEndDate,
      config: {
        ...(examPeriodEndDate.config || {}),
        disabledDates: {
          to: '$$examPeriodStartDate',
        },
      },
    }
  }
  return questions
}

export default function useTermTemplate ({
  school = store.getters.school,
}: TermTemplateOptions = {
  school: store.getters.school,
}) {

  const {
    isLoading,
    baseForm: termTemplate,
    loadBaseTemplate: loadTermTemplate,

  } = useDocumentTemplate({
    type: ref(ATTRIBUTE_ID),
    isSystemType: ref(true),
    school,
  })

  return {
    isLoading,
    termTemplate: ref(termTemplate),
    formatTermTemplateQuestions,
    generateDefaultTemplate,
    generateDefaultPartOfTermTemplate,
    loadTermTemplate,
  }
}
